'use client'

import React, { useEffect, useMemo, useReducer, useRef } from 'react'

import { getServerBasket } from '@/lib/service-api'
import type { Basket, BasketModelInput } from '@/service-api/types.generated'
import { useDeliveryAddress } from '@/stores/useAddressStore'
import { add, fraktProduct, isBagProduct, isFreightProduct } from '@/lib/freight'
import { companySizeTranslationKeyByPath, companyWasteTranslationKeyById } from '@/lib/company-waste'

import { persistToCache, retrieveFromCache } from './cache'
import reducer, { initialState } from './reducer'
import { useTranslations } from 'next-intl'
import { usePathname } from '@/i18n/navigation'

type AttetionCartItem = {
  time: Date
  sku: string
  sizePath?: string
}

export type BasketWithActions = Basket & {
  status: string
  basketModel: BasketModelInput
  totalQuantity: number
  attentionCartItem: AttetionCartItem
  isCompany: boolean
  actions: {
    addVoucherCode: (voucherCode: any) => void
    removeVoucherCode: () => void
    empty: () => void
    addItem: (data: any) => void
    removeItem: (data: any) => void
    incrementItem: (data: any) => void
    decrementItem: (data: any) => void
    drawAttention: (sku: any, sizePath?: any) => void
    retryServerUpdate: () => void
    setCrystallizeOrderId: (crystallizeOrderId: any) => void
    setKlarnaOrderId: (klarnaOrderId: any) => void
  }
}

const BasketContext = React.createContext<BasketWithActions | undefined>(undefined)

export const useBasket = (): BasketWithActions => {
  const context = React.useContext(BasketContext)
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider')
  }
  return context
}

function clientCartItemForCache({
  sku,
  path,
  sizePath,
  quantity,
  priceVariantIdentifier,
  serviceType,
  productType,
}: any) {
  return {
    sku,
    path,
    sizePath,
    quantity,
    priceVariantIdentifier,
    serviceType,
    productType,
  }
}

function clientCartItemForAPI({ sku, path, quantity, priceVariantIdentifier, serviceType, productType }: any) {
  return {
    sku,
    path,
    quantity,
    priceVariantIdentifier,
    serviceType,
    productType,
  }
}

const getCompanyWasteIdFromSku = (sku: string) => sku.split('-')[1] || ''

const getCartItemIdentity = (item: { sku?: string; sizePath?: string }) => `${item?.sku || ''}::${item?.sizePath || ''}`

const getCompanySizeLabelFromPath = (path: string) => {
  const segment = path.split('/').filter(Boolean).pop()?.toLowerCase() || ''
  if (segment === 'tralle') return 'Tralle'
  if (segment === 'standard') return 'Standard'
  if (segment === 'stor') return 'Stor'
  if (segment === 'xxl') return 'XXL'
  return 'iSEKK'
}

const getCompanyImageBySizePath = (path: string) => {
  const segment = path.split('/').filter(Boolean).pop()?.toLowerCase() || ''
  if (segment === 'tralle') return '/waste-bag-size/tralle_isekk.svg'
  if (segment === 'standard') return '/waste-bag-size/standard_isekk.svg'
  if (segment === 'stor') return '/waste-bag-size/large_isekk.svg'
  if (segment === 'xxl') return '/waste-bag-size/XXL_isekk.svg'
  return ''
}

export function BasketProvider({ locale, children }: any) {
  const [{ status, clientBasket, serverBasket, totalQuantity, attentionCartItem }, dispatch] = useReducer(
    reducer,
    initialState,
  )

  const t = useTranslations()
  const pathname = usePathname()
  const deliveryAddress = useDeliveryAddress()
  const prevPostalCodeRef = useRef(deliveryAddress?.postnummer)
  const prevIsCompanyRef = useRef<boolean | null>(null)

  const isCompany = pathname === '/bedrifter' || pathname === '/bedrift-info'

  useEffect(() => {
    // Retrieve cached basket
    ;(async function init() {
      const cache = await retrieveFromCache()
      dispatch({ action: 'hydrate', ...cache })
    })()
  }, [])

  // Mark basket as stale when postal code changes to refetch freight pricing
  useEffect(() => {
    const currentPostalCode = deliveryAddress?.postnummer
    const previousPostalCode = prevPostalCodeRef.current

    // Only trigger if postal code actually changed and we have freight items
    if (
      currentPostalCode !== previousPostalCode &&
      status === 'ready' &&
      clientBasket.cart.some((item: any) => item.sku?.toLowerCase().includes('frakt'))
    ) {
      dispatch({ action: 'retry-server-update' })
    }

    prevPostalCodeRef.current = currentPostalCode
  }, [deliveryAddress?.postnummer, status, clientBasket.cart])

  // Persist the basket on the client
  useEffect(() => {
    if (status !== 'not-hydrated') {
      persistToCache({
        ...clientBasket,
        cart: clientBasket.cart.map(clientCartItemForCache),
      })
    }
  }, [status, clientBasket])

  useEffect(() => {
    if (status === 'not-hydrated') {
      return
    }

    if (prevIsCompanyRef.current === null) {
      prevIsCompanyRef.current = isCompany
      return
    }

    if (prevIsCompanyRef.current !== isCompany) {
      prevIsCompanyRef.current = isCompany

      if (clientBasket.cart.length > 0) {
        dispatch({ action: 'empty' })
      }
    }
  }, [isCompany, status, clientBasket.cart.length])

  /**
   * Define the basketModel object.
   * Used here and in the checkout
   */
  const basketModel = useMemo(() => {
    const model = {
      locale,
      cart: clientBasket.cart.map(clientCartItemForAPI),
      voucherCode: clientBasket.voucherCode,
      crystallizeOrderId: clientBasket.crystallizeOrderId,
      klarnaOrderId: clientBasket.klarnaOrderId,
      postalCode: deliveryAddress?.postnummer || '',
    }

    return model
  }, [locale, clientBasket, deliveryAddress?.postnummer])

  useEffect(() => {
    if (!isCompany || status === 'not-hydrated') {
      return
    }

    const containerItemsInBasket = basketModel.cart.filter((item: any) => {
      const isContainerProductType = item.productType?.toLowerCase() === 'container'
      const isContainerPath = item.path?.toLowerCase().includes('container')

      return isContainerProductType || isContainerPath
    })

    if (containerItemsInBasket.length === 0) {
      return
    }

    containerItemsInBasket.forEach((item: any) => {
      dispatch({ action: 'remove-item', ...item })
    })
  }, [isCompany, status, basketModel.cart])

  // Get server state on cart change
  useEffect(() => {
    let stale = false

    if (isCompany || status !== 'server-basket-is-stale') {
      return
    }

     async function getServerBasketData() {
       try {
         const response = await getServerBasket(basketModel)

         if (!stale && response) {
           dispatch({
             action: 'set-server-basket',
             serverBasket: response.basket,
           })
         }
       } catch (error) {
         console.error(error)
         dispatch({
           action: 'server-update-failed',
         })
       }
     }

    const timeout = setTimeout(getServerBasketData, 250)

    return () => {
      stale = true
      clearTimeout(timeout)
    }
  }, [isCompany, status, locale.crystallizeCatalogueLanguage, basketModel])

  useEffect(() => {
    if (!isCompany || status !== 'server-basket-is-stale') {
      return
    }

    dispatch({ action: 'set-local-ready' })
  }, [isCompany, status])

  function dispatchCartItemAction(action: any) {
    //Do not allow to decrement items below 1
    return (data: any) => {
      if (action == 'decrement-item' && data.quantity == 1) return

      // Skip automatic freight handling for company baskets
      if (!isCompany) {
        //Helper-definitions

        //Check how many bags are in the cart allready
        const numberOfBagsInCart = basketModel?.cart
          .map((item: any) => (isBagProduct(item) ? item.quantity : 0))
          .reduce(add, 0)

        //Figure out how much the difference is based upon the number of bags that are removed by the current action.
        let numberOfBagsToChange = 0
        if (isBagProduct(data) && action === 'remove-item') numberOfBagsToChange -= data.quantity

        if (isBagProduct(data) && action === 'decrement-item') numberOfBagsToChange -= 1

        if (isBagProduct(data) && (action === 'increment-item' || action === 'add-item')) numberOfBagsToChange += 1

        //Calculate number of frakt products that should be in cart
        const numberOfDeliveryProductsToBe = Math.ceil((numberOfBagsInCart + numberOfBagsToChange) / 10)

        //Find how many frakt products there are in the cart
        const numberOfFraktProductsInCart = basketModel?.cart
          .map((item: any) => (isFreightProduct(item) ? item.quantity : 0))
          .reduce(add, 0)

        //If we are simply adding or decrementing one item and we are not at the correc state of frakt-products, adjust the number of fraktproducts as we do bags.
        if (
          ['add-item', 'decrement-item', 'increment-item'].includes(action) &&
          numberOfDeliveryProductsToBe != numberOfFraktProductsInCart
        )
          dispatch({ action, ...fraktProduct })

        //If we remove a whole product (meaning possibly many bags), figure out if we need to remove ALL the bags or simply loop through until we have the correct number of bags.
        if (action === 'remove-item') {
          if (numberOfDeliveryProductsToBe === 0) dispatch({ action, ...fraktProduct })
          else {
            for (let i = numberOfFraktProductsInCart - numberOfDeliveryProductsToBe; i > 0; i--) {
              dispatch({ action: 'decrement-item', ...fraktProduct })
            }
          }
        }
      }

      dispatch({ action, ...data })
    }
  }

  function withLocalState(item: any) {
    // Exclude voucher codes
    if (item.sku.startsWith('--voucher--')) {
      return item
    }

    const clientBasketCartItem = clientBasket.cart.find(
      (c: any) =>
        getCartItemIdentity({ sku: c.sku, sizePath: c.sizePath }) ===
          getCartItemIdentity({ sku: item.sku, sizePath: item.sizePath }) ||
        (c.sku === item.sku && c.path === item.path),
    )

    /**
     * Don't show the cart item if it is not in
     * the client cache.
     **/
    if (!clientBasketCartItem) {
      return null
    }

    if (isCompany) {
      return {
        ...item,
        sku: clientBasketCartItem.sku,
        path: clientBasketCartItem.path || item.path,
        sizePath: clientBasketCartItem.sizePath,
        quantity: clientBasketCartItem.quantity,
      }
    }

    return {
      ...item,
      quantity: clientBasketCartItem.quantity,
    }
  }

  const serverCartWithLocalState = isCompany ? [] : (serverBasket?.cart || []).map(withLocalState).filter(Boolean)

  // Company flow should rely on local basket data only.
  const companyLocalClientItems = isCompany
    ? clientBasket.cart.map((item: any) => {
        const wasteId = getCompanyWasteIdFromSku(item.sku)
        const wasteTranslationKey = companyWasteTranslationKeyById[wasteId]
        const wasteName = wasteTranslationKey ? t(wasteTranslationKey) : wasteId || item.sku
        const sizeSegment = (item.sizePath || item.path || '').split('/').filter(Boolean).pop()?.toLowerCase() || ''
        const sizeTranslationKey = companySizeTranslationKeyByPath[sizeSegment]
        const sizeName = sizeTranslationKey
          ? t(sizeTranslationKey)
          : getCompanySizeLabelFromPath(item.sizePath || item.path || '')
        const sizeImage = getCompanyImageBySizePath(item.sizePath || item.path || '')

        return {
          sku: item.sku,
          quantity: item.quantity,
          path: item.path,
          sizePath: item.sizePath,
          productName: sizeName,
          name: wasteName,
          productImage: sizeImage,
        }
      })
    : []

  const mergedCart = isCompany ? companyLocalClientItems : serverCartWithLocalState

  const companyClientOrderByIdentity = new Map<string, number>(
    clientBasket.cart.map((item: any, index: number) => [
      getCartItemIdentity({ sku: item.sku, sizePath: item.sizePath }),
      index,
    ]),
  )

  const cart = isCompany
    ? [...mergedCart].sort((a: any, b: any) => {
        const aOrder = companyClientOrderByIdentity.get(getCartItemIdentity({ sku: a.sku, sizePath: a.sizePath }))
        const bOrder = companyClientOrderByIdentity.get(getCartItemIdentity({ sku: b.sku, sizePath: b.sizePath }))

        return (aOrder ?? Number.MAX_SAFE_INTEGER) - (bOrder ?? Number.MAX_SAFE_INTEGER)
      })
    : mergedCart

  return (
    <BasketContext.Provider
      value={{
        status,
        basketModel,
        cart,
        total: serverBasket?.total || {},
        totalQuantity,
        attentionCartItem,
        isCompany,
        actions: {
          addVoucherCode: (voucherCode: any) => dispatch({ action: 'add-voucher', voucherCode }),
          removeVoucherCode: () => dispatch({ action: 'remove-voucher' }),
          empty: () => dispatch({ action: 'empty' }),
          addItem: dispatchCartItemAction('add-item'),
          removeItem: dispatchCartItemAction('remove-item'),
          incrementItem: dispatchCartItemAction('increment-item'),
          decrementItem: dispatchCartItemAction('decrement-item'),
          drawAttention: (sku: any, sizePath?: any) => dispatch({ action: 'draw-attention', sku, sizePath }),
          retryServerUpdate: () => dispatch({ action: 'retry-server-update' }),
          setCrystallizeOrderId: (crystallizeOrderId: any) =>
            dispatch({
              action: 'set-crystallize-order-id',
              crystallizeOrderId,
            }),
          setKlarnaOrderId: (klarnaOrderId: any) => dispatch({ action: 'set-klarna-order-id', klarnaOrderId }),
        },
      }}
    >
      {children}
    </BasketContext.Provider>
  )
}
